import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';
import SectionHead from './SectionHead';
import { EASE } from './reveal';

const FAQS = [
  {
    q: 'How long will it take to build my custom barge?',
    a: 'We can build them in 90 days but if there are change orders made along the way they will of course extend that build time. Builds can be expedited but doing so will add cost.',
  },
  {
    q: 'What size is right for my business?',
    a: "If your client's waterways require more mobility go with the 8\u2032 \u00D7 24\u2032. If maneuverability isn't an issue and you want the extra power \u2014 and the ability to push or tow other smaller barges \u2014 go with the 10\u2032 \u00D7 30\u2032.",
  },
  {
    q: 'Can I use different motors?',
    a: 'Of course \u2014 anything can be changed. Changes just tend to add cost and time to the build, but anything can be customized to your liking.',
  },
  {
    q: 'How durable are they? Has a barge ever been punctured?',
    a: 'Extremely durable. We have run numerous tests for leaks and punctures and so far have never had a failure.',
  },
  {
    q: 'Have you ever had any electrical issues? If so, how difficult was it to fix?',
    a: 'On our new barges, no. We use proper wiring and fittings to handle the saltwater and keep the wires up as high in the hull as possible. If there ever is an issue, the hull is designed so someone can get around on the inside for a repair.',
  },
  {
    q: 'What about bilge pumps \u2014 are they installed and working properly?',
    a: 'Yes. Our barges come with bilge pumps that turn on automatically if enough water gets into the bilge to protect it.',
  },
  {
    q: 'What is the solar panel used for? Does it pose any risk of overcharging?',
    a: "It charges the lithium-ion battery used for the barge's electronics. No \u2014 there is a charge controller security system, and the battery is a smart battery as well, so it automatically disconnects once it's fully charged.",
  },
  {
    q: 'Can I change the crane?',
    a: "Yes, but our warranty won't cover your crane and it must use the same mounting system \u2014 otherwise we'll have to figure out what it costs to change that and up-charge accordingly.",
  },
  {
    q: 'Can you paint it my company colors?',
    a: "We can paint your crane arm any color you want, but the HDPE itself doesn't hold paint well \u2014 so anything custom beyond that you'd need to do yourself.",
  },
  {
    q: 'Do you have a trailer for it?',
    a: 'Yes \u2014 trailers can be made available as part of the purchase. New or used, delivery, barge size, etc. all play a role in price, but they tend to vary from $7,000 \u2013 $15,000 depending on size & quality.',
  },
  {
    q: 'How much is your delivery fee?',
    a: "This varies as well, but we won't charge an arm & a leg. We just need to cover gas, potential tires, loading & unloading, and the time for two guys to complete the delivery there and back \u2014 with a small margin for overhead.",
  },
  {
    q: 'Do you offer financing?',
    a: 'Yes, but currently only in Florida. We are still searching for finance options for all other states.',
  },
];

function FaqItem({ item, isOpen, onToggle, index }) {
  const Icon = isOpen ? Minus : Plus;
  return (
    <motion.div
      className={`rounded-xl overflow-hidden bg-black/40 transition-colors ${
        isOpen ? 'border border-cyan-400/60' : 'border border-cyan-400/25 hover:border-cyan-400/50'
      }`}
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.65, ease: EASE, delay: (index % 6) * 0.06 }}
      data-testid={`faq-item-${index}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="group w-full flex items-center justify-between gap-6 text-left px-5 sm:px-7 py-5 sm:py-6"
        data-testid={`faq-question-${index}`}
      >
        <span className="font-display text-white group-hover:text-cyan-300 transition-colors text-base sm:text-lg font-semibold leading-snug tracking-tight">
          {item.q}
        </span>
        <span
          className={`flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full border border-cyan-400/50 flex items-center justify-center text-cyan-300 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        >
          <Icon className="w-4 h-4" />
        </span>
      </button>
      <div
        className={`grid transition-all duration-300 ease-out ${
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <p
            className="px-5 sm:px-7 pb-6 sm:pb-7 text-zinc-400 text-sm leading-relaxed max-w-3xl"
            data-testid={`faq-answer-${index}`}
          >
            {item.a}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative bg-black py-16 sm:py-20 lg:py-32 overflow-hidden border-t border-white/5"
    >
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-5 sm:px-6 lg:px-10">
        <SectionHead
          eyebrow="A Deeper Dive"
          titleLines={['Frequently Asked', <span key="accent" className="text-cyan-300">Questions</span>]}
        />

        <div className="mt-10 sm:mt-14 space-y-3 sm:space-y-4">
          {FAQS.map((item, i) => (
            <FaqItem
              key={item.q}
              index={i}
              item={item}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
